// v2/hooks/useProducts.js — sunucu tarafli urun listesi
// Hiz notlari:
//  - Arama gecikmeli (debounce) gonderilir, her tusa istek atilmaz
//  - Eski istekler AbortController ile iptal edilir (yaris durumu olmaz)
//  - Sayfa degisirken onceki liste ekranda kalir (bos ekran yanip sonmez)

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, qs } from '../utils/api';
import { mapProducts } from '../utils/mapProduct';

const ARAMA_GECIKMESI = 350;
const BOS_ARAMA = { fuzzy: false, corrected: '', aranan: '' };

export default function useProducts({
  page = 1,
  perPage = 24,
  sort = 'stock-desc',
  search = '',
  stock = 'all',
  campaign = 'all',
} = {}) {
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [eurRate, setEurRate] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aramaBilgi, setAramaBilgi] = useState(BOS_ARAMA);

  // Ilk yuklemede spinner, sonraki yuklemelerde soluklastirma icin
  const ilkYukleme = useRef(true);
  const [refreshing, setRefreshing] = useState(false);

  const [geciken, setGeciken] = useState(search);
  const abortRef = useRef(null);

  // Arama terimini geciktir
  useEffect(() => {
    const t = setTimeout(() => setGeciken(search), ARAMA_GECIKMESI);
    return () => clearTimeout(t);
  }, [search]);

  const getir = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    if (ilkYukleme.current) setLoading(true);
    else setRefreshing(true);
    setError(null);

    try {
      const veri = await apiGet(
        `/v2/products/${qs({
          page, per_page: perPage, sort, search: geciken, stock, campaign,
        })}`,
        { signal: ctrl.signal }
      );
      if (ctrl.signal.aborted) return;

      setProducts(mapProducts(veri && veri.results));
      setTotalPages(Number(veri && veri.total_pages) || 1);
      setTotalItems(Number(veri && veri.total_items) || 0);
      setEurRate(Number(veri && veri.euro_rate) || 0);
      setAramaBilgi({
        fuzzy: !!(veri && veri.fuzzy),
        corrected: (veri && veri.corrected) || '',
        aranan: (veri && veri.searched) || '',
      });
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      setError(e.message || 'Ürünler alinamadi');
      setProducts([]);
      setAramaBilgi(BOS_ARAMA);
    } finally {
      if (!ctrl.signal.aborted) {
        ilkYukleme.current = false;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [page, perPage, sort, geciken, stock, campaign]);

  useEffect(() => {
    getir();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [getir]);

  return {
    products,
    totalPages,
    totalItems,
    eurRate,
    loading,
    refreshing,
    error,
    aramaBilgi,
    yenile: getir,
    aramaBekliyor: search !== geciken,
  };
}
